import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, AlertCircle, ArrowLeft, User, MapPin } from 'lucide-react';
import { authenticateUser, signupUser } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  
  
  const [activeTab, setActiveTab] = useState('login');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If redirecting from somewhere, let's just make sure activeTab is 'login' initially.
  
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const res = await authenticateUser(email, password);
    setLoading(false);
    
    if (res && res.success) {
      if (!res.user.role) res.user.role = 'user';
      login(res.user);
      navigate('/admin/dashboard');
    } else {
      setError(res?.message || 'Login failed: The server response was invalid.');
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!name || !email || !password) {
      setError('Please fill all required fields.');
      return;
    }

    setLoading(true);
    const res = await signupUser({ name, email, password, address });
    setLoading(false);

    if (res.success) {
      const loginRes = await authenticateUser(email, password);
      if (loginRes && loginRes.success) {
        if (!loginRes.user.role) loginRes.user.role = 'user';
        login(loginRes.user);
        navigate('/admin/dashboard');
      } else {
        alert("Registration successful! You may now login.");
        setActiveTab('login');
        setPassword('');
      }
    } else {
      setError(res.message || 'Signup failed.');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1514222049077-df903dce311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')" }}>
      {/* Dark overlay for beautiful effect */}
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"></div>

      <div className="relative sm:mx-auto sm:w-full sm:max-w-md z-10 text-center">
        <h2 className="mt-6 text-3xl font-extrabold text-white drop-shadow-lg">
          Nathnagri Portal
        </h2>
        <p className="mt-2 text-sm text-stone-200">
          Join the community or access your dashboard.
        </p>
      </div>

      <div className="relative mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="bg-white/95 backdrop-blur-xl shadow-2xl sm:rounded-2xl border border-white/20 overflow-hidden">
          
          {/* Tab Handles */}
          <div className="flex border-b border-stone-200">
            <button
              className={`flex-1 py-4 text-sm font-bold text-center transition-colors ${activeTab === 'login' ? 'bg-white text-saffron-600 border-b-2 border-saffron-600' : 'bg-stone-50 text-stone-500 hover:text-stone-700 hover:bg-stone-100'}`}
              onClick={() => { setActiveTab('login'); setError(''); }}
            >
              Sign In
            </button>
            <button
              className={`flex-1 py-4 text-sm font-bold text-center transition-colors ${activeTab === 'signup' ? 'bg-white text-saffron-600 border-b-2 border-saffron-600' : 'bg-stone-50 text-stone-500 hover:text-stone-700 hover:bg-stone-100'}`}
              onClick={() => { setActiveTab('signup'); setError(''); }}
            >
              Create Account
            </button>
          </div>

          <div className="py-8 px-6 sm:px-10">
            {error && (
              <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-xl flex items-start border border-red-200">
                <AlertCircle className="w-5 h-5 mr-3 mt-0.5 shrink-0" />
                <p className="text-sm font-semibold">{error}</p>
              </div>
            )}

            <AnimatePresence mode="wait">
              {activeTab === 'login' ? (
                <motion.form 
                  key="login"
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                  onSubmit={handleLoginSubmit} className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1">Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-stone-400" />
                      </div>
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron-500 bg-stone-50 text-stone-900 placeholder-stone-400" placeholder="admin@nathnagri.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1">Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-stone-400" />
                      </div>
                      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron-500 bg-stone-50 text-stone-900 placeholder-stone-400" placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <button type="submit" disabled={loading} className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-gradient-to-r from-saffron-600 to-saffron-500 hover:from-saffron-700 hover:to-saffron-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-saffron-500 transition-all disabled:opacity-70">
                    {loading ? 'Authenticating...' : 'Sign In'}
                  </button>
                  
                  <div className="mt-8 border-t border-stone-100 pt-6">
                    <h4 className="text-xs font-bold uppercase text-stone-400 mb-3 tracking-wider">Test Accounts:</h4>
                    <p className="text-sm text-stone-600 font-medium">Admin: <code className="bg-stone-100 px-1 py-0.5 rounded text-saffron-600">admin@nathnagri.com</code></p>
                  </div>
                </motion.form>
              ) : (
                <motion.form 
                  key="signup"
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.2 }}
                  onSubmit={handleSignupSubmit} className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1">Full Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><User className="h-5 w-5 text-stone-400" /></div>
                      <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron-500 bg-stone-50 text-stone-900 placeholder-stone-400" placeholder="Rahul Singh"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1">Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Mail className="h-5 w-5 text-stone-400" /></div>
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron-500 bg-stone-50 text-stone-900 placeholder-stone-400" placeholder="rahul@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1">Address (Optional)</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><MapPin className="h-5 w-5 text-stone-400" /></div>
                      <input type="text" value={address} onChange={(e) => setAddress(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron-500 bg-stone-50 text-stone-900 placeholder-stone-400" placeholder="Bareilly, UP"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1">Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Lock className="h-5 w-5 text-stone-400" /></div>
                      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron-500 bg-stone-50 text-stone-900 placeholder-stone-400" placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <button type="submit" disabled={loading} className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-gradient-to-r from-stone-800 to-stone-900 hover:from-black hover:to-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-900 transition-all disabled:opacity-70">
                    {loading ? 'Creating Account...' : 'Sign Up'}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>

            <div className="mt-8 text-center">
              <Link to="/" className="inline-flex items-center justify-center text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors bg-stone-100 hover:bg-stone-200 px-4 py-2 rounded-lg">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
